/**
 * MessageList — virtualized list of messages for the active thread.
 *
 * Renders at most {@link RENDER_CAP} message rows (the most recent 200) via
 * `@tanstack/react-virtual`, satisfying the virtualization cap from
 * Requirement 11.6 and Property 29. Older messages stay in the underlying
 * thread record (and are still summarized into the rolling summary), but the
 * DOM only ever holds a virtualized window over the most-recent suffix.
 *
 * Auto-scroll behaviour: when the visible message count grows the list
 * scrolls the newest row into view so freshly arrived assistant tokens and
 * just-sent user messages are immediately visible (Requirement 11.2).
 *
 * The `<div role="log" aria-live="polite">` wrapper makes streaming token
 * arrivals discoverable to assistive tech without barging in mid-utterance
 * (Requirement 10.2, 10.3).
 *
 * Validates: Requirements 11.6, 11.2.
 */

import { useEffect, useRef, useCallback } from 'react';
import type { ReactElement } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useThreadsStore } from '../../state';
import type { ChatPipeline } from '../../infra/chatPipeline';

import styles from './MessageList.module.css';
import { MessageRow } from './MessageRow';

interface PipelineGlobal {
  __pipeline?: ChatPipeline;
}

/**
 * Hard cap on simultaneously rendered messages. Older messages remain in the
 * persisted thread but are not mounted into the DOM (Requirement 11.6).
 */
const RENDER_CAP = 200;

/** Estimated row height used as the initial measurement before each row is
 *  observed. Real heights are measured via `virtualizer.measureElement`. */
const ESTIMATED_ROW_HEIGHT = 80;

/** Extra rows kept above and below the viewport — keeps fast scrolls smooth. */
const OVERSCAN = 8;

export function MessageList(): ReactElement {
  const activeThread = useThreadsStore((s) => s.activeThread);
  const messages = activeThread?.messages ?? [];

  // Show the most recent RENDER_CAP messages.
  const startIdx = Math.max(0, messages.length - RENDER_CAP);
  const visibleMessages = messages.slice(startIdx);

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: visibleMessages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  // Auto-scroll to the newest row when the visible count changes.
  useEffect(() => {
    if (visibleMessages.length === 0) return;
    virtualizer.scrollToIndex(visibleMessages.length - 1, { align: 'end' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMessages.length]);

  // Retry: find the last user message before the failed assistant message
  // and re-send it through the pipeline.
  const handleRetry = useCallback((failedAssistantId: string) => {
    const pipeline = (globalThis as PipelineGlobal).__pipeline;
    if (!pipeline) return;

    const allMessages = activeThread?.messages ?? [];
    const failedIdx = allMessages.findIndex((m) => m.id === failedAssistantId);
    if (failedIdx === -1) return;

    // Find the last user message before the failed assistant message.
    let lastUserMsg = null;
    for (let i = failedIdx - 1; i >= 0; i--) {
      if (allMessages[i]?.role === 'user') {
        lastUserMsg = allMessages[i];
        break;
      }
    }
    if (lastUserMsg === null || lastUserMsg === undefined) return;

    void pipeline.send(lastUserMsg.content, {
      imageData: lastUserMsg.imageData ?? null,
    });
  }, [activeThread]);

  const totalSize = virtualizer.getTotalSize();
  const items = virtualizer.getVirtualItems();

  return (
    <div
      ref={parentRef}
      className={styles.messageList}
      role="log"
      aria-live="polite"
      aria-relevant="additions text"
      data-testid="message-list"
    >
      <div
        className={styles.inner}
        style={{ height: `${totalSize}px` }}
      >
        {items.map((virtualRow) => {
          const message = visibleMessages[virtualRow.index];
          if (!message) return null;
          return (
            <div
              key={message.id}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              className={styles.virtualRow}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              <MessageRow message={message} onRetry={handleRetry} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
